import React, { useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import { useTheme } from '@material-ui/core/styles'
import {
  Button,
  Box,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  FormControl,
  Grid,
  LinearProgress,
  MenuItem,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from '@material-ui/core'
import EventBusyOutlinedIcon from '@material-ui/icons/EventBusyOutlined'
import { DateTime } from 'luxon'
import RouterLinkButton from 'components/router-link-button'
import BlockedTimesEmpty from './blocked-times-empty'
import useStyles from './block-time-manager.styles'
import BlockTimeFormDialog from './block-time-form-dialog'
import BlockTimeItem from './block-time-item/block-type-item'
import { gtmEvent } from 'lib/gtm'

const GET_BLOCK_TIME = gql`
  query coachAvailabilityBlockTimes($datetimeRange: DateTimeRangeInput) {
    coachAvailabilityBlockTimes(datetimeRange: $datetimeRange) {
      id
      startTime
      endTime
      allDay
    }
  }
`

const breakIntoDateTime = blockTimes => {
  return blockTimes.map(time => {
    return {
      id: time.id,
      startDate: DateTime.fromISO(time.startTime).toFormat('MMMM d'),
      startTime: time.allDay
        ? 'All day'
        : DateTime.fromISO(time.startTime).toFormat('t'),
      endDate: DateTime.fromISO(time.endTime).toFormat('MMMM d'),
      endTime: time.allDay
        ? 'All day'
        : DateTime.fromISO(time.endTime).toFormat('t'),
    }
  })
}

const oneMonthLater = isoTime => {
  const dt = DateTime.fromISO(isoTime)
  return dt.endOf('Month')
}

const formatFromIsoDate = isoTime => {
  const dt = DateTime.fromISO(isoTime)
  return dt.toFormat('MMMM yyyy')
}

const makeInitialDateArray = () => {
  const initialDateTime = DateTime.local().startOf('Month')
  const monthYears = [initialDateTime.toISO()]
  let dateTime = initialDateTime
  while (monthYears.length < 12) {
    dateTime = dateTime.plus({ months: 1 }).startOf('Month')
    monthYears.push(dateTime.toISO())
  }
  return monthYears
}

const BlockTimeManager = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const classes = useStyles()
  const [showGcBlockedTimes, setShowGcBlockedTimes] = useState(false)

  const [selectedStart, setSelectedStart] = useState(
    DateTime.local().startOf('Month'),
  )

  const { loading, error, data, refetch } = useQuery(GET_BLOCK_TIME, {
    errorPolicy: 'all',
    variables: {
      datetimeRange: {
        from: selectedStart,
        to: oneMonthLater(selectedStart),
      },
    },
  })

  const monthYears = makeInitialDateArray()

  if (loading) {
    return (
      <Card variant="outlined" className={classes.card}>
        <CardHeader
          title="Set your blocked times"
          titleTypographyProps={{ variant: 'subtitle1' }}
          className={classes.header}
        />
        <CardContent className={classes.content}>
          <LinearProgress color="primary" />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card variant="outlined" className={classes.card}>
        <CardHeader
          title="Error getting blocked times"
          titleTypographyProps={{ variant: 'subtitle1' }}
          className={classes.header}
        />
        <CardContent className={classes.content}>
          <LinearProgress variant="determinate" value="100" color="secondary" />
        </CardContent>
      </Card>
    )
  }

  const onDestroySuccess = () => {
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'bookings',
      formAction: 'delete-block-time',
      formStatus: 'success',
    })
    refetch()
  }

  const onCreateSuccess = () => {
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'bookings',
      formAction: 'create-block-time',
      formStatus: 'success',
    })
    refetch()
  }

  const blockTimes = breakIntoDateTime(data.coachAvailabilityBlockTimes)

  const changeShowGcBlockedTimes = () => {
    setShowGcBlockedTimes(!showGcBlockedTimes)
  }

  const title = (
    <Grid container>
      <Grid item xs={12}>
        <Typography variant="h5" className={classes.cardHeader}>
          <Box fontWeight={500}>Blocked times</Box>
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography
          variant="body1"
          color="textSecondary"
          className={classes.headerExplainerText}
        >
          Override your availability by blocking busy times on your calendar.
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <RouterLinkButton
          color="primary"
          size="small"
          component="a"
          to="/pga-coach/settings/integrations"
        >
          Pull blocked times from my external calendar
        </RouterLinkButton>
        <Grid container style={{ flexWrap: 'nowrap', alignItems: 'center' }}>
          <Switch
            className={classes.switcher}
            checked={showGcBlockedTimes}
            onChange={changeShowGcBlockedTimes}
          />
          <Typography variant="body2">
            Show external calendar blocked times
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={12} md={6}>
        <Box mt={2}>
          <FormControl variant="outlined">
            <TextField
              select
              value={selectedStart}
              variant="outlined"
              label="Select a month"
              onChange={e => setSelectedStart(e.target.value)}
            >
              {monthYears.map(dT => (
                <MenuItem key={dT} value={dT}>
                  {formatFromIsoDate(dT)}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
        </Box>
      </Grid>
    </Grid>
  )

  return (
    <Card elevation={0} style={{ padding: 0 }}>
      <CardHeader
        title={title}
        titleTypographyProps={{ variant: 'subtitle1' }}
        className={classes.header}
        action={
          !isMobile && (
            <BlockTimeFormDialog
              trigger={
                <Button variant="contained" color="primary">
                  Create Block
                </Button>
              }
              initialSelectedDay="TUESDAY"
              initialTimeSlots={[]}
              onSuccess={onCreateSuccess}
            />
          )
        }
      />
      {isMobile && (
        <CardActions style={{ paddingLeft: 0, paddingRight: 0 }}>
          <BlockTimeFormDialog
            trigger={
              <Button variant="contained" color="primary" fullWidth="true">
                Create Block
              </Button>
            }
            initialSelectedDay="TUESDAY"
            initialTimeSlots={[]}
            onSuccess={onCreateSuccess}
          />
        </CardActions>
      )}

      <CardContent className={classes.content}>
        {blockTimes.length > 0 ? (
          blockTimes.map(time => (
            <BlockTimeItem
              key={time.id}
              time={time}
              onDestroySuccess={onDestroySuccess}
              showGcBlockedTimes={showGcBlockedTimes}
            />
          ))
        ) : (
          <BlockedTimesEmpty
            IconComp={() => <EventBusyOutlinedIcon fontSize="large" />}
            title="No blocked times"
            caption="You currently have not blocked any times for this month."
          />
        )}
      </CardContent>
    </Card>
  )
}

export default BlockTimeManager
